import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../App';
import AiLocked from '../components/AiLocked';
import { Loader2, Briefcase, CheckCircle2, ArrowLeft } from 'lucide-react';

export default function MockInterviewPage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [jobRole, setJobRole] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [category, setCategory] = useState('general');
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    axios.get(`${API}/ai/usage`).then(r => setUsage(r.data.mock_interview)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const start = async () => {
    if (!jobRole.trim()) { toast.error('Enter a target job role'); return; }
    setStarting(true); setFeedback(null);
    try {
      const r = await axios.post(`${API}/ai/mock-interview/start`, { job_role: jobRole, difficulty, category });
      setSession(r.data);
      setAnswers(r.data.questions.map(() => ''));
      setUsage(u => ({ ...u, remaining: r.data.remaining }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start interview');
    } finally { setStarting(false); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { session_id: session.session_id, answers: session.questions.map((q, i) => ({ question: q, answer: answers[i] })) };
      const r = await axios.post(`${API}/ai/mock-interview/submit`, payload);
      setFeedback(r.data.feedback);
      toast.success('Interview evaluated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not evaluate');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" /></div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Briefcase className="w-6 h-6 text-[#CE1126]" />
          <h1 className="text-2xl font-bold">AI Mock Interview</h1>
        </div>

        {!usage?.has_access ? (
          <AiLocked title="AI Mock Interview" />
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4" data-testid="mock-remaining">
              Sessions remaining: <strong>{usage.remaining}</strong> of {usage.limit}
            </p>

            {!session && (
              <Card className="p-6 space-y-4" data-testid="mock-setup">
                <div>
                  <label className="text-sm font-medium">Target Job Role</label>
                  <input value={jobRole} onChange={e => setJobRole(e.target.value)} placeholder="e.g. Sales Executive"
                    className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1" data-testid="mock-role-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Difficulty</label>
                    <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1" data-testid="mock-difficulty">
                      <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Focus</label>
                    <select value={category} onChange={e => setCategory(e.target.value)} className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm mt-1" data-testid="mock-category">
                      <option value="general">General</option><option value="behavioural">Behavioural</option><option value="technical">Technical</option>
                    </select>
                  </div>
                </div>
                <Button className="btn-uae-red" onClick={start} disabled={starting || usage.remaining <= 0} data-testid="mock-start-btn">
                  {starting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : 'Start Interview'}
                </Button>
                {usage.remaining <= 0 && <p className="text-xs text-red-600">No sessions remaining in your package.</p>}
              </Card>
            )}

            {session && !feedback && (
              <Card className="p-6 space-y-5" data-testid="mock-questions">
                {session.questions.map((q, i) => (
                  <div key={i}>
                    <p className="font-medium text-sm mb-1">Q{i + 1}. {q}</p>
                    <textarea value={answers[i]} onChange={e => { const a = [...answers]; a[i] = e.target.value; setAnswers(a); }}
                      rows={3} placeholder="Type your answer…" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid={`mock-answer-${i}`} />
                  </div>
                ))}
                <Button className="btn-uae-green" onClick={submit} disabled={submitting} data-testid="mock-submit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Evaluating…</> : 'Submit for Feedback'}
                </Button>
              </Card>
            )}

            {feedback && (
              <div className="space-y-4" data-testid="mock-feedback">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">Overall Score</h3>
                    <span className="text-3xl font-bold text-[#CE1126]">{feedback.overall_score}</span>
                  </div>
                  <p className="text-sm text-gray-700">{feedback.summary}</p>
                  <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs font-semibold text-[#009639] mb-1">Strengths</p>
                      <ul className="text-sm list-disc pl-4 space-y-1">{(feedback.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#CE1126] mb-1">Improvements</p>
                      <ul className="text-sm list-disc pl-4 space-y-1">{(feedback.improvements || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                    </div>
                  </div>
                </Card>
                {(feedback.per_question || []).map((p, i) => (
                  <Card key={i} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm font-medium flex-1">{p.question}</p>
                      <span className="text-sm font-bold text-gray-700">{p.score}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1"><CheckCircle2 className="w-3.5 h-3.5 inline text-[#009639] mr-1" />{p.feedback}</p>
                    {p.model_answer_tip && <p className="text-xs text-gray-500 mt-1 italic">Tip: {p.model_answer_tip}</p>}
                  </Card>
                ))}
                <Button variant="outline" onClick={() => { setSession(null); setFeedback(null); }} data-testid="mock-restart-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" />New Interview
                </Button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
